import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import moment, { Moment } from 'moment';
import { DatamatrixReaderComponent, TypeScan } from 'src/app/fragments/datamatrix-reader/datamatrix-reader.component';
import { DisplayEprescriptionFragment } from 'src/app/fragments/modal/display-eprescription/display-eprescription.fragment';
import { EnvoiEprescription, InfoConsultationEprescription, InfoRechercheEprescription } from 'src/app/fragments/modal/envoi-eprescription/envoi-eprescription.fragment';
import { FeatureKey, FeatureService } from 'src/app/services/feature/feature.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { HeaderCommunicationService } from 'src/app/services/node/header-communication.service';
import { EPresciption } from 'src/app/services/ordonnance-numerique/connecteur-models';
import { RechercheResultatModel } from 'src/app/services/ordonnance-numerique/ordonnance-numerique.service';
import { PatientService } from 'src/app/services/patient/patient.service';
import { Header } from 'src/app/utils/interfaces/new/header.interface';

interface NgSelectItem {
  id: string;
  libelle: string;
}

const SearchFields = [
  'datePrescriptionBorneBasse',
  'datePrescriptionBorneHaute',
  'dateExecutionBorneBasse',
  'dateExecutionBorneHaute',
  'dateModificationBorneBasse',
  'dateModificationBorneHaute',
  'typePrescription',
  'soustypePrescription',
  'nirBenef',
  'nirOD',
  'nirODDateNaissance',
  'nirODDateRang',
];

@Component({
  selector: 'app-e-prescription',
  templateUrl: './e-prescription.component.html',
  styleUrls: ['./e-prescription.component.scss']
})
export class EPrescriptionComponent implements OnInit {

  public formGroup: FormGroup;
  public formGroupDirect: FormGroup;

  public typesPrescription: Array<NgSelectItem> = [];
  public typesPrescriptionAll: Array<NgSelectItem> = [];
  public soustypesPrescription: Array<NgSelectItem> = [];
  public dataSource: MatTableDataSource<RechercheResultatModel>;

  public displayedColumns = ['type', 'soustype', 'numero', 'patientNirOD', 'patientNir', 'patientDateNaissance', 'patientRangNaissance']

  public canUseEprescriptionFeature = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _modalSrv: ModalService,
    private _view: ViewContainerRef,
    private _send: HeaderCommunicationService,
    private _featureSrv: FeatureService,
    private _router: Router,
  ) { }

  async ngOnInit(): Promise<void> {

    const canUseEprescriptionFeature = await this._initFeatureFlipping();
    if (!canUseEprescriptionFeature) {
      this._router.navigateByUrl('/user/utilitaires');
      return;
    }

    const head: Header = {
      backPage: 'utilitaires',
      isBack: true,
      title: 'Recherche et Consultation d\'E-Prescription',
    };
    this._send.announceHeader(head);


    this._initForm();
    this._initLists();
  }

  private async _initFeatureFlipping(): Promise<boolean> {
    this.canUseEprescriptionFeature = await this._featureSrv.canUseFeature(FeatureKey.E_PRESCRIPTION);
    return this.canUseEprescriptionFeature;
  }

  private _initForm() {
    const obj = {};
    for (const field of SearchFields) {
      if (field === 'typePrescription') {
        obj[field] = [undefined, Validators.required];
      } else if (field === 'nirBenef' || field === 'nirOD') {
        obj[field] = [undefined, [Validators.minLength(13), Validators.maxLength(13)]]; // Validators.pattern(PatientService.REXEG_NIR_13)
      } else {
        obj[field] = undefined;
      }
    }
    this.formGroup = this._formBuilder.group(obj, { validators: dateValidator });

    this.formGroupDirect = this._formBuilder.group({
      uniqueId: [undefined, [Validators.required]],
    });
  }

  private _initLists(): void {
    this.typesPrescription = [
      { id: 'PRODUITS_SANTE', libelle: 'Produits de santé' },
      { id: 'BIO', libelle: 'Analyse de biologie médicale' },
      { id: 'KINE', libelle: 'Kinésithérapeute' },
      { id: 'INFIRMIER', libelle: 'Infirmier' },
      { id: 'PEDICURE', libelle: 'Pédicure' },
      { id: 'ORTHOPHONISTE', libelle: 'Orthophoniste' },
      { id: 'ORTHOPTISTE', libelle: 'Orthoptiste' },
    ];

    this.typesPrescriptionAll = [...this.typesPrescription, { id: 'ALL', libelle: 'Tous les types' }];

    this.soustypesPrescription = [
      { id: 'ALD', libelle: 'ALD' },
      { id: 'MEX', libelle: 'Médicament d\'exception' },
      { id: 'BZN', libelle: 'Bizone' },
      { id: 'OSC', libelle: 'Produits sécurisés' },
    ];
  }

  public async openQrCodeReader(): Promise<void> {
    const modal = this._modalSrv.openModal<DatamatrixReaderComponent>(this._view, DatamatrixReaderComponent);
    modal.instance.readMode = TypeScan.EPRESCRIPTION;

    const sub = modal.instance.closeEmitter.subscribe(async (resp: any | undefined) => {
      if (resp) {
        this.formGroupDirect.get('uniqueId').setValue(resp);
        this.consulterEprescriptions();
      }
      modal.destroy();
      sub.unsubscribe();
    });
  }

  public async rechercherEprescriptions(): Promise<void> {

    const setData = (data: Array<RechercheResultatModel>): void => {
      this.dataSource = new MatTableDataSource(data);
    };

    let uncompletedField = false;
    for (const field of SearchFields) {
      if (this.formGroup?.get(field)?.value) {
        uncompletedField = true;
        break;
      }
    }
    if (!uncompletedField) {
      this._modalSrv.openAlert(
        this._view, 'Recherche incomplète', 'Au moins un champ doit être renseigner pour lancer une recherche d\'ordonnances numériques'
      );
      return;
    }

    const datePrescriptionBorneBasse = this.formGroup?.get('datePrescriptionBorneBasse')?.value;
    const datePrescriptionBorneHaute = this.formGroup?.get('datePrescriptionBorneHaute')?.value;
    const dateExecutionBorneBasse = this.formGroup?.get('dateExecutionBorneBasse')?.value;
    const dateExecutionBorneHaute = this.formGroup?.get('dateExecutionBorneHaute')?.value;
    const dateModificationBorneBasse = this.formGroup?.get('dateModificationBorneBasse')?.value;
    const dateModificationBorneHaute = this.formGroup?.get('dateModificationBorneHaute')?.value;
    let typePrescription = this.formGroup?.get('typePrescription')?.value;
    const soustypePrescription = this.formGroup?.get('soustypePrescription')?.value;
    const nirBenef = this.formGroup?.get('nirBenef')?.value;
    const nirOD = this.formGroup?.get('nirOD')?.value;
    const nirODDateNaissance = this.formGroup?.get('nirODDateNaissance')?.value;
    const nirODDateRang = this.formGroup?.get('nirODDateRang')?.value;

    if (typePrescription === 'ALL') {
      typePrescription = this.typesPrescription.map(t => t.id);
    } else {
      typePrescription = [typePrescription];
    }

    // Alert avant de lancer une recherche ALL pour indiquer que cela va prendre du temps
    if (typePrescription.length > 1) {
      const modal = this._modalSrv.openModalConfirmation(
        this._view, 'Recherche longue', 'La recherche de tous les types d\'ordonnances numériques peut prendre du temps.\n\nVoulez-vous continuer ?'
      );
      await new Promise<void>((resolve) => {
        const sub = modal.instance.closeEmitter.subscribe((resp: any | undefined) => {
          if (resp) {
            resolve();
          }
          sub.unsubscribe();
          modal.destroy();
        })
      });
    }

    try {
      const modalRecherche = this._modalSrv.openModal(
        this._view, EnvoiEprescription,
        [
          {
            name: 'recherche',
            value: {
              datePrescriptionBorneBasse: datePrescriptionBorneBasse ? moment((datePrescriptionBorneBasse as Moment))?.toDate() : undefined,
              datePrescriptionBorneHaute: datePrescriptionBorneHaute ? moment((datePrescriptionBorneHaute as Moment))?.toDate() : undefined,
              dateExecutionBorneBasse: dateExecutionBorneBasse ? moment((dateExecutionBorneBasse as Moment))?.toDate() : undefined,
              dateExecutionBorneHaute: dateExecutionBorneHaute ? moment((dateExecutionBorneHaute as Moment))?.toDate() : undefined,
              dateModificationBorneBasse: dateModificationBorneBasse ? moment((dateModificationBorneBasse as Moment))?.toDate() : undefined,
              dateModificationBorneHaute: dateModificationBorneHaute ? moment((dateModificationBorneHaute as Moment))?.toDate() : undefined,
              typePrescription: typePrescription || undefined,
              soustypePrescription: soustypePrescription || undefined,
              nirBenef: nirBenef || undefined,
              nirOD: nirOD || undefined,
              nirODDateNaissance: nirODDateNaissance || undefined,
              nirODDateRang: nirODDateRang || undefined,
            }
          }
        ],
      );

      const sub = modalRecherche.instance.closeEmitter.subscribe((r: InfoRechercheEprescription) => {
        setData(r.resultatRecherche);
        modalRecherche.destroy();
        sub.unsubscribe();
      });
    } catch (e) {
      // TODO Insert coin
    }
  }

  consulterEprescriptions(): void {
    if(this.formGroupDirect && this.formGroupDirect.get('uniqueId')?.value) {
      this.displayPrescription({
        prescriptionNumero: this.formGroupDirect.get('uniqueId')?.value
      });
    }
  }

  public async displayPrescription(evt: RechercheResultatModel|Partial<RechercheResultatModel>): Promise<void> {
    try {
      const modalRecherche = this._modalSrv.openModal(
      this._view, EnvoiEprescription,
      [
        {
          name: 'consultation',
          value: {
            uniqueId: evt?.prescriptionNumero || undefined,
          }
        }
      ],
      );

      const elemConsult = await new Promise<EPresciption>((resolve, reject) => {
        const sub = modalRecherche.instance.closeEmitter.subscribe((r: InfoConsultationEprescription) => {
          if (r?.resultatConsultation) {
            resolve(r.resultatConsultation);
          } else {
            reject();
          }
          modalRecherche.destroy();
          sub.unsubscribe();
        });
      });

      const modaleDisplay = this._modalSrv.openModal(
        this._view, DisplayEprescriptionFragment,
        [
          {
            name: 'prescription',
            value: elemConsult
          }
        ],
      );

      const sub = modaleDisplay.instance.closeEmitter.subscribe((r: InfoConsultationEprescription) => {
        modaleDisplay.destroy();
        sub.unsubscribe();
      });

    } catch (e) {
      this._modalSrv.openAlert(this._view, 'Erreur', 'Une erreur est survenue lors de la consultation de l\'ordonnance numérique');
    }
  }

}

export const dateValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
  const amplitude = 60;

  let hasErrors = false;
  const errors: any = {};

  const datePrescriptionBorneBasse = control.get('datePrescriptionBorneBasse')?.value;
  let datePrescriptionBorneHaute = control.get('datePrescriptionBorneHaute')?.value;
  const dateExecutionBorneBasse = control.get('dateExecutionBorneBasse')?.value;
  let dateExecutionBorneHaute = control.get('dateExecutionBorneHaute')?.value;
  const dateModificationBorneBasse = control.get('dateModificationBorneBasse')?.value;
  let dateModificationBorneHaute = control.get('dateModificationBorneHaute')?.value;

  const isDatePrescriptionFilled = datePrescriptionBorneBasse || datePrescriptionBorneHaute;
  const isDateExecutionFilled = dateExecutionBorneBasse || dateExecutionBorneHaute;
  const isDateModificationFilled = dateModificationBorneBasse || dateModificationBorneHaute;

  // Une fois qu'on a testé filled, on peut completer les bornes hautes manquantes
  datePrescriptionBorneHaute = datePrescriptionBorneHaute || moment().add(1, 'days').toDate()
  dateExecutionBorneHaute = dateExecutionBorneHaute || moment().add(1, 'days').toDate()
  dateModificationBorneHaute = dateModificationBorneHaute || moment().add(1, 'days').toDate()

  if ((isDatePrescriptionFilled && (isDateExecutionFilled || isDateModificationFilled)) ||
      (isDateModificationFilled && (isDatePrescriptionFilled || isDateExecutionFilled)) ||
      (isDateExecutionFilled && (isDatePrescriptionFilled || isDateModificationFilled))) {
    hasErrors = true;
    errors.dateConflict = true;
  }

  // On vérifier si il n'y a que les bornes hautes de renseignées
  if ((isDatePrescriptionFilled && !datePrescriptionBorneBasse) ||
      (isDateExecutionFilled && !dateExecutionBorneBasse) ||
      (isDateModificationFilled && !dateModificationBorneBasse)) {
    hasErrors = true;
    errors.dateRangeIncomplete = true;
  }

  if ((isDatePrescriptionFilled && moment(datePrescriptionBorneHaute).diff(moment(datePrescriptionBorneBasse), 'days') > amplitude) ||
      (isDateExecutionFilled && moment(dateExecutionBorneHaute).diff(moment(dateExecutionBorneBasse), 'days') > amplitude) ||
      (isDateModificationFilled && moment(dateModificationBorneHaute).diff(moment(dateModificationBorneBasse), 'days') > amplitude)) {
    hasErrors = true;
    errors.dateRangeExceeded = true;
  }

  return hasErrors ? errors : null;
};
